import { useState } from "react";
import { BACKEND_URL } from "../constants";
import { Scene, UpdateSceneRequest } from "../types/types";
import { useListAny } from ".";

interface UsePutResult<T> {
  isLoading: boolean;
  error: Error | null;
  put: (body: UpdateSceneRequest) => Promise<T | null>;
}

interface UseDeleteResult<T> {
  isLoading: boolean;
  error: Error | null;
  deleteFn: (sceneId: string) => Promise<T | null>;
}

export const useFetchScenes = () => {
  return useListAny<Scene[]>('/scenes');
}

export const useFetchScene = (sceneId: string) => {
  return useListAny<Scene>(`/scenes/${sceneId}`);
}

export function useUpdateScene<T = any>(): UsePutResult<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const put = async (body: UpdateSceneRequest): Promise<T | null> => {
    if (!body.sceneId) {
      setError(new Error("ID is required"));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");

      const response = await fetch(`${BACKEND_URL}/scenes/${body.sceneId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ?? "",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = (await response.json()) as T;
      return data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, put };
}

export function useDeleteScene<T = any>(): UseDeleteResult<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteFn = async (sceneId: string): Promise<T | null> => {
    if (!sceneId) {
      setError(new Error("ID is required"));
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");

      const response = await fetch(`${BACKEND_URL}/scenes/${sceneId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ?? "",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = (await response.json()) as T;
      return data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, deleteFn };
}